import { createFeatureSelector, createSelector } from '@ngrx/store';
import type { IBiomapRasterLayerDescriptor } from '../../../interfaces/biomap/IBiomapLayer';
import type { IRasterLayer } from '../../../interfaces/sjmap2/raster/IRaster';
import { _selectExplorerSJMap } from '../explorer/explorer-sjmap.selector';
import {
  BIOMAP_FEATURE_KEY,
  BiomapState,
} from '../../reducers/biomap/biomap.reducer';
import { getBiomapRasterLayerDescriptor } from '../../../utils/biomap/biomap-layer.utils';

export const selectBiomapState = createFeatureSelector<BiomapState>(BIOMAP_FEATURE_KEY);

export const selectBiomapHabitatClassification = createSelector(
  selectBiomapState,
  (state) => state.habitatClassification,
);

export const selectBiomapMsaComponent = createSelector(
  selectBiomapState,
  (state) => state.msaComponent,
);

export const selectBiomapRasterLayerDescriptors = createSelector(
  _selectExplorerSJMap,
  (sjmap) =>
    (sjmap?.layers ?? [])
      .filter((layer): layer is IRasterLayer => layer.type === 'raster')
      .map(getBiomapRasterLayerDescriptor)
      .filter((descriptor): descriptor is IBiomapRasterLayerDescriptor => descriptor !== null),
);

export const selectBiomapHabitatRasterLayers = createSelector(
  selectBiomapRasterLayerDescriptors,
  (descriptors) => descriptors.filter(({ identity }) => identity.product === 'habitat'),
);

export const selectBiomapMsaRasterLayers = createSelector(
  selectBiomapRasterLayerDescriptors,
  (descriptors) => descriptors.filter(({ identity }) => identity.product === 'msa'),
);

export const selectActiveBiomapHabitatRasterLayer = createSelector(
  selectBiomapHabitatRasterLayers,
  selectBiomapHabitatClassification,
  (descriptors, classification) =>
    descriptors.find(
      ({ identity }) => identity.habitatClassification === classification,
    )?.layer ?? null,
);

export const selectActiveBiomapMsaRasterLayer = createSelector(
  selectBiomapMsaRasterLayers,
  selectBiomapMsaComponent,
  (descriptors, component) =>
    descriptors.find(({ identity }) => identity.msaComponent === component)?.layer ?? null,
);

export const selectBiomapExplorerKpiByLayer = (rasterLayerUuid: string) => createSelector(
  selectBiomapState,
  (state) => state.explorerKpi.entities[rasterLayerUuid] ?? null,
);

export const selectBiomapExplorerKpiLoadingByLayer = (rasterLayerUuid: string) => createSelector(
  selectBiomapState,
  (state) => state.explorerKpi.loading[rasterLayerUuid] ?? false,
);

export const selectBiomapExplorerKpiErrorByLayer = (rasterLayerUuid: string) => createSelector(
  selectBiomapState,
  (state) => state.explorerKpi.errors[rasterLayerUuid] ?? null,
);

export const selectBiomapSelectedProtectedAreaUuid = createSelector(
  selectBiomapState,
  (state) => state.geospatial.selectedProtectedAreaUuid,
);

export const selectBiomapSelectedMsaHighValueAreaId = createSelector(
  selectBiomapState,
  (state) => state.geospatial.selectedMsaHighValueAreaId,
);

export const selectBiomapProtectedAreasResponse = createSelector(
  selectBiomapState,
  (state) => state.protectedAreas.response,
);

export const selectBiomapProtectedAreasLoading = createSelector(
  selectBiomapState,
  (state) => state.protectedAreas.loading,
);

export const selectBiomapProtectedAreasError = createSelector(
  selectBiomapState,
  (state) => state.protectedAreas.error,
);
